/**
 * Branch Add Calendar - Branch admin interface for creating new branch calendars
 * Adapted from AddCalendar for branch-level use
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import apiService from '../../services/api';
import { AdminPageLayout, FormInput, FormTextArea, FormSelect, FormCheckbox, FormColorPicker, Button, FormActions, Badge } from '../../components';
import { ErrorBanner } from '../../components/common';
import { useFormValidation } from '../../hooks/useFormValidation';
import '../admin/CalendarForm.css';

const BranchAddCalendar = () => {
  const navigate = useNavigate();
  const { branch_id } = useParams();
  const [error, setError] = useState(null);

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Mountain Time - Arizona (no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
  ];

  const tagOptions = [
    'recreational', 'competitive', 'youth', 'adult', 'camps', 'clinics',
    'indoor', 'outdoor', 'travel', 'premier', 'development'
  ];

  const {
    formData,
    errors,
    touched,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      name: '',
      description: '',
      is_active: true,
      color_code: '#1565C0',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      season_id: '',
      tags: []
    },
    validationSchema: (values) => {
      const newErrors = {};

      if (!values.name.trim()) {
        newErrors.name = 'Calendar name is required';
      }

      return newErrors;
    },
    onSubmit: async (values) => {
      try {
        const calendarData = {
          name: values.name,
          description: values.description,
          scope: 'branch',
          scope_id: branch_id,  // Set the branch ID
          visibility_roles: ['all'],  // everyone can see by default
          is_active: values.is_active,
          color_code: values.color_code,
          timezone: values.timezone,
          season_id: values.season_id || null,  // Include season_id
          tags: Array.isArray(values.tags) ? values.tags : []
        };

        console.log('Creating branch calendar with data:', calendarData);

        await apiService.createCalendar(calendarData);
        navigate(`/branch/${branch_id}/calendars`);
      } catch (err) {
        console.error('Calendar creation error:', err);
        setError(err.message || 'Failed to create calendar');
        throw err;
      }
    }
  });

  const handleTagToggle = (tag) => {
    const currentTags = formData.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    setFieldValue('tags', newTags);
  };

  return (
    <AdminPageLayout
      title="New Branch Calendar"
      subtitle="Create a new calendar for your branch"
      backPath={`/branch/${branch_id}/calendars`}
    >
      <div className="add-calendar-page">
        {error && (
          <ErrorBanner
            type="error"
            message={error}
            onDismiss={() => setError(null)}
          />
        )}

        <form onSubmit={handleFormSubmit} className="calendar-form">
          <div className="form-section">
            <h3>Calendar Details</h3>

            <FormInput
              label="Calendar Name"
              name="name"
              value={formData.name}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.name ? errors.name : ''}
              placeholder="e.g., Branch Training Schedule, Branch Events"
              required
            />

            <FormTextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.description ? errors.description : ''}
              placeholder="Describe the calendar purpose and schedule"
              rows={3}
            />

            <FormInput
              label="Season (Optional)"
              name="season_id"
              value={formData.season_id}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.season_id ? errors.season_id : ''}
              placeholder="Leave blank to associate with a season later"
              help="Dates will come from the associated season"
            />
          </div>

          <div className="form-section">
            <h3>Visibility Settings</h3>

            <div className="visibility-info">
              This calendar will be visible to all members of your branch. You can customize visibility roles after creating the calendar.
            </div>
          </div>

          <div className="form-section">
            <h3>Calendar Settings</h3>

            <div className="form-row">
              <FormColorPicker
                label="Color Code"
                name="color_code"
                value={formData.color_code}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.color_code ? errors.color_code : ''}
              />

              <FormSelect
                label="Timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={touched.timezone ? errors.timezone : ''}
                options={timezones}
              />
            </div>

            <FormCheckbox
              label="Active"
              name="is_active"
              checked={formData.is_active}
              onChange={(e) => setFieldValue('is_active', e.target.checked)}
            />
            <p className="field-hint" style={{ marginTop: '-12px', marginLeft: '28px' }}>
              Only active calendars are visible and can be used
            </p>
          </div>

          <div className="form-section">
            <h3>Tags</h3>
            <div className="tag-selector">
              {tagOptions.map(tag => (
                <Badge
                  key={tag}
                  variant={formData.tags.includes(tag) ? 'primary' : 'outline'}
                  size="md"
                  className="tag-badge"
                  onClick={() => handleTagToggle(tag)}
                  role="switch"
                  aria-pressed={formData.tags.includes(tag)}
                  aria-label={`${tag} tag`}
                  style={{ cursor: 'pointer' }}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <FormActions
              onCancel={() => navigate(`/branch/${branch_id}/calendars`)}
              submitLabel={isSubmitting ? 'Creating...' : 'Create Calendar'}
              isSubmitting={isSubmitting}
              submitIcon={<Check size={16} />}
              cancelIcon={<X size={16} />}
            >
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(`/branch/${branch_id}/calendars`)}
                disabled={isSubmitting}
              >
                <X size={16} style={{ marginRight: '8px' }} />
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                <Check size={16} style={{ marginRight: '8px' }} />
                {isSubmitting ? 'Creating...' : 'Create Calendar'}
              </Button>
            </FormActions>
          </div>
        </form>
      </div>
    </AdminPageLayout>
  );
};

export default BranchAddCalendar;
